/** Payroll export dialog: tick staff, pick a date range (quick picks or custom), download CSV. */
import React, { useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { iso, addDays, startOfWeek } from './utils';

const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const monthEnd = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const QUICK = [
  ['this_month', 'This month', () => { const n = new Date(); return [monthStart(n), monthEnd(n)]; }],
  ['last_month', 'Last month', () => { const n = new Date(); const p = new Date(n.getFullYear(), n.getMonth() - 1, 1); return [p, monthEnd(p)]; }],
  ['this_week', 'This week', () => { const s = startOfWeek(new Date()); return [s, addDays(s, 6)]; }],
  ['last_week', 'Last week', () => { const s = addDays(startOfWeek(new Date()), -7); return [s, addDays(s, 6)]; }],
];

export function AttendanceExportDialog({ open, onOpenChange, people, defaultUserId }) {
  const [sel, setSel] = useState(() => new Set(defaultUserId ? [defaultUserId] : []));
  const [[from, to], setRange] = useState(() => QUICK[0][2]().map(iso));
  const [quick, setQuick] = useState('this_month');
  const [busy, setBusy] = useState(false);
  const allOn = people.length > 0 && sel.size === people.length;
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pick = (k, fn) => { setQuick(k); setRange(fn().map(iso)); };
  const days = useMemo(() => (from && to ? Math.round((new Date(to) - new Date(from)) / 86400000) + 1 : 0), [from, to]);

  const download = async () => {
    if (!sel.size) return toast.error('Tick at least one person');
    if (!from || !to || days < 1) return toast.error('Pick a valid date range');
    setBusy(true);
    try {
      const qs = new URLSearchParams({ start: from, end: to, user_ids: allOn ? '' : [...sel].join(',') });
      const r = await fetch(`${API_URL}/api/timesheets/clock/export.csv?${qs}`, { credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Export failed');
      const blob = await r.blob();
      const name = /filename="([^"]+)"/.exec(r.headers.get('Content-Disposition') || '')?.[1] || `attendance_${from}_to_${to}.csv`;
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: name });
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${name}`);
      onOpenChange(false);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="att-export-dialog">
        <DialogHeader>
          <DialogTitle>Export attendance for payroll</DialogTitle>
          <DialogDescription>One row per person per day — clock in, clock out, breaks, on-site hours, logged hours, late minutes and where they were — with a totals row each.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-bold text-slate-700">Who</div>
              <button type="button" className="text-xs font-semibold text-sky-700 hover:underline" onClick={() => setSel(allOn ? new Set() : new Set(people.map((p) => p.user_id)))} data-testid="att-export-all">{allOn ? 'Clear all' : 'Select all'}</button>
            </div>
            <div className="max-h-44 overflow-y-auto rounded-lg border divide-y" data-testid="att-export-people">
              {people.map((p) => (
                <label key={p.user_id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={sel.has(p.user_id)} onChange={() => toggle(p.user_id)} className="w-4 h-4 accent-gray-900" data-testid={`att-export-person-${p.user_id}`} />
                  <span className="font-medium text-slate-800">{p.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-bold text-slate-700 mb-1.5">When</div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="att-export-quick">
              {QUICK.map(([k, label, fn]) => (
                <button key={k} type="button" onClick={() => pick(k, fn)} data-testid={`att-export-quick-${k}`}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${quick === k ? 'bg-gray-900 border-gray-900 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-gray-500'}`}>{label}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-600">From<input type="date" value={from} onChange={(e) => { setQuick('custom'); setRange([e.target.value, to]); }} className="mt-1 h-9 w-full rounded-md border px-2 text-sm" data-testid="att-export-from" /></label>
              <label className="text-xs text-slate-600">To<input type="date" value={to} min={from} onChange={(e) => { setQuick('custom'); setRange([from, e.target.value]); }} className="mt-1 h-9 w-full rounded-md border px-2 text-sm" data-testid="att-export-to" /></label>
            </div>
            <div className="text-[11px] text-slate-500 mt-1" data-testid="att-export-summary">{sel.size} {sel.size === 1 ? 'person' : 'people'} · {days > 0 ? `${days} day${days === 1 ? '' : 's'}` : 'pick a range'}</div>
          </div>

          <Button onClick={download} disabled={busy} className="w-full bg-gray-900 hover:bg-black text-white h-11" data-testid="att-export-download">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} Download CSV
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
